<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">

          <BackButton
              :label="$t('general.button.back')"
              :to="{ name: 'coursePrivateLearningCoachingList' }"
              marginBottom="5"/>

          <div class="d-flex justify-content-between">
            <h6 class="element-header">
              {{ $t('course.general_title') }}
            </h6>
          </div>

          <div class="element-box">
            <h5 class="form-header">
              {{ $t('course.private_learning_coaching.title') }}
            </h5>

            <div v-if="isLoaded">
              <form @submit.prevent="submitForm" novalidate>
                <div class="row">
                  <div class="col-12">
                    <AuditInfo
                        :user-role="currentUserRole"
                        :created-by-user-data="formData.createdByUserData"
                        :created-at="formData.createdAt"
                        :updated-by-user-data="formData.updatedByUserData"
                        :updated-at="formData.updatedAt"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <BaseInput v-model="formData.name"
                               label="Bezeichnung"
                               :required=true
                               :validation="v$.name"
                               :messages="v$.name.required.$message">
                    </BaseInput>
                    <div v-if="operationTypeDropdown && operationTypeDropdown.length">
                      <SelectVue
                          v-model="formData.operationType"
                          :options="operationTypeDropdown"
                          @update:modelValue="updateOperationType"
                          :is-clear="true"
                          :required="true"
                          label="Maßnahmenart"
                          placeholder="Maßeinheitentyp auswählen"
                          :validation="v$.operationType"
                          :messages="v$.operationType.required.$message"
                      />
                    </div>
                    <BaseInput v-model="formData.actionNumber"
                               label="Maßnahmennummer"
                               :required=true
                               :validation="v$.actionNumber"
                               :messages="v$.actionNumber.required.$message">
                    </BaseInput>
                  </div>
                  <div class="col-lg-6 col-md-6 col-sm-12">
                    <div class="row">
                      <div class="col-lg-6 md-12 sm-12">
                        <Datepicker
                            v-model="formData.startDate"
                            :required="true"
                            label="Gültigkeitsstart"
                            position="down"
                            :validation="v$.startDate"
                            :messages="v$.startDate.required.$message"/>
                      </div>
                      <div class="col-lg-6 md-12 sm-12">
                        <Datepicker
                            v-model="formData.endDate"
                            :required="true"
                            label="Gültigkeitsende"
                            position="down"
                            :validation="v$.endDate"
                            :messages="v$.endDate.required.$message"/>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-lg-6 md-12 sm-12">
                        <BaseNumber v-model="formData.units"
                                    label="Einheiten (Gesamtanzahl)"
                                    :required=true
                                    disabled="disabled"
                                    :validation="v$.units"
                                    :messages="v$.units.required.$message">
                        </BaseNumber>
                      </div>
                      <div class="col-lg-6 md-12 sm-12">
                        <BaseNumber v-model="formData.cost"
                                    :isCurrency=true
                                    label="Umsatz pro Einheit"
                                    :decimalPlaces="4"
                                    :required=true
                                    :validation="v$.cost"
                                    :messages="v$.cost.required.$message">
                        </BaseNumber>
                      </div>
                      <div class="col-12">
                        <div v-if="officeDropdown && officeDropdown.length">
                          <SelectVue
                              v-model="formData.officeId"
                              :options="officeDropdown"
                              :is-clear="true"
                              :required="true"
                              label="Standort"
                              :validation="v$.officeId"
                              :messages="v$.officeId.required.$message"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div v-if="clientDropdown && clientDropdown.length">
                      <SelectVue
                          v-model="formData.clientIds"
                          :options="clientDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          :label="$t('placeholder.client_name')"
                          :validation="v$.clientIds"
                          :messages="v$.clientIds.required.$message"
                      />
                    </div>
                  </div>
                  <div class="col-12">
                    <div v-if="moduleDropdown && moduleDropdown.length">
                      <SelectVue
                          v-model="formData.moduleIds"
                          :options="moduleDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          label="Module"
                          :validation="v$.moduleIds"
                          :messages="v$.moduleIds?.required?.$message"
                      />
                    </div>
                  </div>
                </div>
                <div class="form-buttons-w text-center">
                  <SaveButton
                      :label="$t('general.button.save')"
                      :loading="isSubmitting"/>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Sidebar></Sidebar>
    </div>
  </div>
</template>

<script setup>
import coursePrivateLearningCoachingService from '@/api/Course/PrivateLearningCoaching';
import dropdownService from '@/api/Dropdown';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import CreateButton from '@/components/CreateButton.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import Sidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { getClientDropdown, getOfficeDropdown } from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import useVuelidate from '@vuelidate/core';
import 'daterangepicker';
import 'daterangepicker/daterangepicker.css';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const { t }                 = useI18n();
const piniaUserState        = piniaUserStore();
const currentUserRole       = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const piniaEnumsState       = piniaEnumsStore();
const getEnums              = computed(() => piniaEnumsState.enums);
const operationTypeDropdown = ref([]);
const officeDropdown        = ref([]);
const clientDropdown        = ref([]);
const moduleDropdown        = ref([]);
const isSubmitting          = ref(false);
const route                 = useRoute();
const courseId              = route.params.id;
const isLoaded              = ref(false);

const formData = ref({
  nameOf: '',
  operationType: '',
  actionNumber: '',
  duration: '',
  units: 0,
  cost: 0,
  startDate: '',
  endDate: '',
  officeId: '',
  clientIds: [],
  moduleIds: [],
});

const rules = ref({
  name: requiredRule(t),
  operationType: requiredRule(t),
  actionNumber: requiredRule(t),
  units: requiredRule(t),
  cost: requiredRule(t),
  startDate: requiredRule(t),
  endDate: requiredRule(t),
  officeId: requiredRule(t),
  clientIds: requiredRule(t),
  moduleIds: '',
});

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    if (courseId) {
      await coursePrivateLearningCoachingService.putCourse(formData.value, +courseId);
    } else {
      await coursePrivateLearningCoachingService.postCourse(formData.value);
    }

    await router.push({ name: 'coursePrivateLearningCoachingList' });

    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getCourse() {
  try {
    const { data }    = await coursePrivateLearningCoachingService.getCourse(+courseId);
    const clients     = data.data.clients || [];
    const modules     = data.data?.modules || [];
    const officeId    = data.data?.location.id;
    const clientArray = clients.map(client => client?.id).filter(id => id != null);
    const moduleArray = modules.map(module => module?.id).filter(id => id != null);

    formData.value = { ...data.data, officeId: officeId, clientIds: clientArray, moduleIds: moduleArray };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getModuleDropdown(moduleDropdown) {
  try {
    const { data }       = await dropdownService.getCoursePrivateLearningCoachingModule();
    moduleDropdown.value = data.data.map(item => ({
      label: `${item.name}`,
      value: item?.id,
    }));
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getOperationTypeDropdown() {
  operationTypeDropdown.value =getEnums?.value?.privateLearningCoachingOperationTypes || []
}

onMounted(async () => {
  await Promise.all([
    getOperationTypeDropdown(),
    getOfficeDropdown(officeDropdown),
    getClientDropdown(clientDropdown),
    getModuleDropdown(moduleDropdown),
  ]);

  if (courseId) {
    await getCourse();
  }

  isLoaded.value = true;
});

</script>

<style scoped>
</style>